/**
 * Purpose:
 *   Trigger CSRF-protected logout using browser form POST navigation.
 *
 * Notes:
 *   - Uses form submission (no fetch) to preserve redirect flow to Keycloak.
 */

'use client';

import { useCallback, useState } from 'react';
import { useToast } from '@/components/ui/toast/hooks/useToast';

export interface UseLogoutReturn {
  /** Execute logout action */
  logout: () => Promise<void>;
  /** Whether logout is in progress */
  isLoading: boolean;
}

const LOGOUT_CSRF_COOKIE = 'logout_csrf';

function readCookie(name: string): string | null {
  const encodedName = `${encodeURIComponent(name)}=`;
  const entries = document.cookie.split(';');

  for (const entry of entries) {
    const trimmed = entry.trim();
    if (trimmed.startsWith(encodedName)) {
      return decodeURIComponent(trimmed.slice(encodedName.length));
    }
  }

  return null;
}

export function useLogout(): UseLogoutReturn {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useToast();

  const logout = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);

    const csrfToken = readCookie(LOGOUT_CSRF_COOKIE);

    if (!csrfToken) {
      addToast({
        type: 'warning',
        message: 'Your session is stale. Please sign in again.',
        duration: 5000,
      });
      setIsLoading(false);
      window.location.assign('/login');
      return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/auth/logout';
    form.style.display = 'none';

    const csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrfToken';
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    document.body.appendChild(form);
    form.submit();
  }, [addToast, isLoading]);

  return { logout, isLoading };
}

